import { toast } from 'react-toastify';

import { simulateContract, writeContract } from '@wagmi/core';

import type { Address, ContractFunctionArgs, ContractFunctionName } from 'viem';
import { type Abi } from 'viem';

// eslint-disable-next-line deprecate/import
import { wagmiConfig } from '@shared/common/providers/LegacyWeb3Provider/wagmi';
import { namedWeb3Errors } from '@shared/constants';
import displayWalletErrorToast from '@shared/helpers/displayWalletErrorToast';
import { useApproveIfNeeded } from '@shared/helpers/web3/erc20';
import { getEstimateGasPrice } from '@shared/helpers/web3/getEstimateGasPrice';
import getWalletActionStyles from '@shared/helpers/web3/getWalletActionStyles';
import { handleTransactionResult } from '@shared/helpers/web3/handleTransactionResult';
import useWeb3 from '@shared/helpers/web3/hooks/useWeb3';
import type { TransactionReceiptWithLinkToExplorer } from '@shared/helpers/web3/trackAndNotifyTransactionStatus';
import useMagicIdUserWalletInfo from '@shared/hooks/useMagicIdUserWalletInfo';

type AbiType = Abi | readonly unknown[];
type FunctionMutability = 'nonpayable' | 'payable';
type FunctionNameType<TAbi extends AbiType> = ContractFunctionName<TAbi, FunctionMutability>;

type ExecuteProps<
  TAbi extends AbiType,
  TFunctionName extends FunctionNameType<TAbi>,
  TAgs extends ContractFunctionArgs<TAbi, FunctionMutability, TFunctionName>,
> = {
  props: {
    abi: TAbi;
    contractAddress: Address;
    functionName: TFunctionName;
    chainId: number;
    args?: TAgs;
    approve?: {
      amount: number;
      tokenAddress: Address;
      tokenDecimals: number;
      amountToCheck?: number;
    };
    signDetails?: {
      message: string;
    };
  };
  options?: {
    toastMessage?: string;
    onSuccess?: (txWithLink?: TransactionReceiptWithLinkToExplorer) => void;
  };
};

export const useWriteContractExecutor = () => {
  const { getUserWalletType } = useMagicIdUserWalletInfo();
  const { requestSign } = useWeb3();
  const { approveIfNeeded } = useApproveIfNeeded();

  const execute = async <
    TAbi extends AbiType,
    TFunctionName extends FunctionNameType<TAbi>,
    TAgs extends ContractFunctionArgs<TAbi, FunctionMutability, TFunctionName>,
  >({
    props,
    options,
  }: ExecuteProps<TAbi, TFunctionName, TAgs>) => {
    const toastId = Date.now();
    const { toastMessage = 'Processing your transaction...', onSuccess } = options ?? {};

    try {
      const { userWallet, walletType } = getUserWalletType();

      toast.loading(toastMessage, {
        toastId,
        style: getWalletActionStyles(walletType),
      });

      if (props.signDetails) {
        const signDetails = await requestSign({
          walletType,
          targetAddress: userWallet.attributes.address,
          message: props.signDetails.message,
          signType: 'SIWE',
        });

        if (!signDetails) {
          return;
        }
      }

      if (props.approve) {
        await approveIfNeeded({
          chainId: props.chainId,
          amount: props.approve.amount,
          spenderAddress: props.contractAddress,
          tokenAddress: props.approve.tokenAddress,
          tokenDecimals: props.approve.tokenDecimals,
          amountToCheck: props.approve.amountToCheck ?? props.approve.amount,
        });
      }

      const estimatedGasPrice = await getEstimateGasPrice({
        abi: props.abi,
        contractAddress: props.contractAddress,
        functionName: props.functionName,
        chainId: props.chainId,
        args: props.args,
        walletAddress: userWallet.attributes.address,
      });

      if (!estimatedGasPrice) {
        throw new Error('Estimate gas error');
      }

      const { gasPrice, gasEstimate } = estimatedGasPrice;

      const { request } = await simulateContract(wagmiConfig, {
        abi: props.abi,
        address: props.contractAddress,
        functionName: props.functionName,
        chainId: props.chainId,
        args: (props.args ?? []) as readonly unknown[],
        gas: gasEstimate,
        ...(gasPrice && { gasPrice }),
      });

      const hash = await writeContract(wagmiConfig, request);

      const result = await handleTransactionResult(hash, props.chainId);

      if (onSuccess) {
        onSuccess(result);
      }

      return result;
    } catch (error) {
      displayWalletErrorToast({
        error,
        unknownExceptionReplaceMessage: namedWeb3Errors.UNKNOWN_TRANSACTION_ERROR,
      });
      return null;
    } finally {
      toast.dismiss(toastId);
    }
  };

  return { execute };
};
